/**
 * Shared logic for building NEX match options in the Flagged Item modal.
 * Used by both modal body and footer so option list and "already matched" exclusion stay in sync.
 */
import type { InvoiceDetailResponse, InvoicePart } from '../../types/invoice';
import { globalPartIndexForMatchedNexLines } from '../../utils/invoiceGlobalPartIndex';
import { getNexLineDisplayQty, getSignedNexLineDisplayQty, type NexLineQuantityBasis } from '../../utils/nexLineQuantity';

export type InvoicePartWithNex = InvoicePart & {
    poHeaderID?: number;
    lineNumber?: number;
    unqSeq?: number;
    _poOrderedDate?: string;
    lineStatus?: string;
    /** CIECA part type code from NEX (e.g. PAL used, PAA aftermarket). */
    ciecaCode?: string;
    /** Nexsyis part type text (e.g. "Parts Aftermarket", "Glass"). */
    partType?: string;
    _poVendorName?: string;
    _nexMatchSource?: 'returns' | 'orders';
    /** NEX `qtyRec` for Flagged Item display (received qty on PO line). */
    qtyRec?: number;
    /** NEX `qtyOnOrder` for Flagged Item display when org uses on-order basis. */
    qtyOnOrder?: number;
    /** Reconciliation matchable qty before consumption (see API `fullQty`). */
    fullQty?: number;
    consumed?: number;
};

/** NEX quantity for Flagged Item rows. Uses only the org basis (`qtyOnOrder` or `qtyRec`). */
export function nexQtyReceivedForDisplay(
    opt: InvoicePartWithNex,
    basis: NexLineQuantityBasis = 'qtyRec',
    invoiceType?: 'received' | 'returned' | 'return_slip' | 'sublet',
): number {
    const ext = opt as unknown as Record<string, unknown>;
    const qtyOnOrder = opt.qtyOnOrder ?? (typeof ext.qty_on_order === 'number' ? ext.qty_on_order : undefined);
    const qtyRec = opt.qtyRec ?? (typeof ext.qty_rec === 'number' ? ext.qty_rec : undefined);
    const qtyReturned = typeof ext.qtyReturned === 'number' ? ext.qtyReturned : undefined;
    const displayed = getNexLineDisplayQty({
        qtyOnOrder,
        qtyRec,
        qtyReturned,
        qty: opt.qty,
        quantity: typeof ext.quantity === 'number' ? ext.quantity : undefined,
    }, invoiceType, basis);
    const isReturnInvoice = invoiceType === 'returned' || invoiceType === 'return_slip';
    if (isReturnInvoice) return displayed;
    const basisQty = basis === 'qtyOnOrder' ? qtyOnOrder : qtyRec;
    if (basisQty != null && Number.isFinite(basisQty)) return displayed;
    const fullQty = opt.fullQty ?? (typeof ext.full_qty === 'number' ? ext.full_qty : undefined);
    if (fullQty != null && Number.isFinite(fullQty)) return fullQty;
    return displayed;
}

export function getNexKey(nex: any): string {
    const poHeaderID = nex.poHeaderID ?? nex.po_header_id ?? '';
    const lineNumber = nex.lineNumber ?? nex.line_number ?? '';
    const unqSeq = nex.unqSeq ?? nex.unq_seq ?? '';
    return `${poHeaderID}-${lineNumber}-${unqSeq}-${nex.partNumber}`;
}

/**
 * Saved `matchedNexLines.partIndex` is the last-saved line index. Map it onto the current
 * row, or return undefined when that row was deleted so its NEX line can be suggested again.
 * Freight and other non-line indexes are left unchanged.
 */
export function currentIndexForStoredLineMatch(
    storedPartIndex: number,
    savedLineCount: number,
    lineItemBackendIndexByCurrentIndex: (number | undefined)[] | undefined,
): number | undefined {
    const isSavedLineItem = storedPartIndex >= 0 && storedPartIndex < savedLineCount;
    if (!isSavedLineItem || !lineItemBackendIndexByCurrentIndex) return storedPartIndex;
    const current = lineItemBackendIndexByCurrentIndex.findIndex((backendIdx) => backendIdx === storedPartIndex);
    return current >= 0 ? current : undefined;
}

function normalizeVendorName(value: string | null | undefined): string {
    return (value ?? '')
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** Loose vendor match for scoping NEX rows to the invoice supplier (same idea as server probable-match). */
function vendorNamesLikelyMatch(a: string | null | undefined, b: string | null | undefined): boolean {
    const na = normalizeVendorName(a);
    const nb = normalizeVendorName(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    if (na.includes(nb) || nb.includes(na)) return true;
    const tokensA = na.split(' ').filter((t) => t.length >= 3);
    const tokensB = new Set(nb.split(' ').filter((t) => t.length >= 3));
    if (tokensA.length === 0 || tokensB.size === 0) return false;
    const overlap = tokensA.filter((t) => tokensB.has(t)).length;
    return overlap >= Math.min(2, tokensA.length);
}

function parsePreferredPoHeaderId(raw: string | null | undefined): number | null {
    if (raw == null || String(raw).trim() === '') return null;
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length < 5) return null;
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : null;
}

function mapDetailLineToInvoicePartWithNex(
    dl: any,
    po: any,
    vendorName: string,
    invoiceType: InvoiceDetailResponse['invoiceType'] | undefined,
    nexMatchSource?: 'returns' | 'orders',
    nexLineQuantityBasis: NexLineQuantityBasis = 'qtyRec',
): InvoicePartWithNex {
    const matchSource = nexMatchSource ?? (dl?._nexMatchSource as 'returns' | 'orders' | undefined);
    const qtyRecRaw = dl?.qtyRec;
    const qtyRec = qtyRecRaw != null && Number.isFinite(Number(qtyRecRaw)) ? Number(qtyRecRaw) : undefined;
    const qtyOnOrderRaw = dl?.qtyOnOrder;
    const qtyOnOrder =
        qtyOnOrderRaw != null && Number.isFinite(Number(qtyOnOrderRaw)) ? Number(qtyOnOrderRaw) : undefined;
    return {
        partNumber: String(dl?.partNumber ?? ''),
        title: String(dl?.partDesc ?? ''),
        qty: getSignedNexLineDisplayQty(dl, invoiceType, nexLineQuantityBasis),
        qtyRec,
        qtyOnOrder,
        listPrice: Number(dl?.listPrice ?? 0),
        unitBuyPrice: Number(dl?.unitCost ?? 0),
        totalBuyPrice: Number(dl?.extendedCost ?? 0),
        confidence: 1,
        poHeaderID: dl?.poHeaderID ?? po?.id,
        lineNumber: dl?.lineNumber,
        unqSeq: dl?.unqSeq,
        _poOrderedDate: typeof po?.orderedDate === 'string' ? po.orderedDate : undefined,
        lineStatus: typeof dl?.lineStatus === 'string' ? dl.lineStatus : undefined,
        ...(typeof dl?.ciecaCode === 'string' && dl.ciecaCode.trim()
            ? { ciecaCode: dl.ciecaCode.trim() }
            : {}),
        ...(typeof dl?.partType === 'string' && dl.partType.trim()
            ? { partType: dl.partType.trim() }
            : {}),
        _poVendorName: vendorName,
        ...(matchSource ? { _nexMatchSource: matchSource } : {}),
    };
}

export type BuildFullNexOptionsOptions = {
    /**
     * When true (default), only POs whose vendor matches invoice supplier and/or whose PO header id
     * matches slip `purchaseOrderNumber` are included. When false, include every PO on the RO.
     */
    scopeToInvoiceContext?: boolean;
    /** Org setting: which NEX qty field to show/use for received/sublet (returns ignore this). */
    nexLineQuantityBasis?: NexLineQuantityBasis;
};

type NexVendorBucket = { vendorName: string; lines: InvoicePart[]; noRemaining?: boolean };

/**
 * When there is no active vendor-name mismatch, the Flagged Item UI should use the unified
 * single-vendor table only — never the multi-vendor accordion. Collapse multiple buckets into one
 * row list (scoped to invoice supplier when we can match buckets, otherwise all lines deduped).
 */
function collapseNexOptionsByVendorWhenSupplierResolved(
    buckets: NexVendorBucket[] | undefined,
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse>
): NexVendorBucket[] | undefined {
    if (!buckets?.length) return buckets;
    if (invoice?.reconciliationIssues?.vendorNameMismatch) return buckets;
    if (buckets.length <= 1) return buckets;

    const supplierTrim = (editedFields.supplierText ?? invoice?.supplierText ?? '').trim();
    let chosen = buckets.filter((b) => supplierTrim.length > 0 && vendorNamesLikelyMatch(supplierTrim, b.vendorName));
    if (chosen.length === 0 && supplierTrim.length > 0) {
        const ns = normalizeVendorName(supplierTrim);
        chosen = buckets.filter((b) => normalizeVendorName(b.vendorName) === ns);
    }
    const sectionsToMerge = chosen.length > 0 ? chosen : buckets;
    const rawMerged = sectionsToMerge.flatMap((s) => s.lines ?? []);
    const seen = new Set<string>();
    const mergedLines: InvoicePart[] = [];
    for (const l of rawMerged) {
        const k = getNexKey(l);
        if (seen.has(k)) continue;
        seen.add(k);
        mergedLines.push(l);
    }
    const noRemaining =
        sectionsToMerge.some((s) => s.noRemaining) && mergedLines.length === 0;
    const displayVendor = supplierTrim || sectionsToMerge[0]?.vendorName || buckets[0].vendorName;
    return [{ vendorName: displayVendor, lines: mergedLines, ...(noRemaining ? { noRemaining: true } : {}) }];
}

function poMatchesInvoiceContext(
    po: any,
    supplierTrim: string,
    preferredPoHeaderID: number | null
): boolean {
    const vendorName = String(po?.vendorName ?? '').trim();
    const poHeaderNumeric = Number(po?.id);
    const matchesPo =
        preferredPoHeaderID != null &&
        preferredPoHeaderID > 0 &&
        Number.isFinite(poHeaderNumeric) &&
        poHeaderNumeric === preferredPoHeaderID;
    const matchesSupplier =
        supplierTrim.length > 0 && vendorNamesLikelyMatch(supplierTrim, vendorName);
    const noHints =
        supplierTrim.length === 0 && (preferredPoHeaderID == null || preferredPoHeaderID <= 0);
    return matchesPo || matchesSupplier || noHints;
}

/**
 * NEX detail lines from stored `nexResponse`, grouped by vendor.
 * With default scoping: only the supplier-matching vendor and/or the PO from `purchaseOrderNumber`.
 * Return slips: lines are tagged as `/purchasing/returns` for display.
 */
export function buildFullNexOptionsByVendorFromInvoice(
    invoice: InvoiceDetailResponse | undefined,
    editedFields: Partial<InvoiceDetailResponse> = {},
    buildOpts?: BuildFullNexOptionsOptions
): Array<{ vendorName: string; lines: InvoicePartWithNex[]; noRemaining?: boolean }> {
    const rawReturns = invoice?.nexResponse;
    if (!Array.isArray(rawReturns)) return [];

    const scopeToInvoiceContext = buildOpts?.scopeToInvoiceContext !== false;
    const supplierTrim = (editedFields.supplierText ?? invoice?.supplierText ?? '').trim();
    const preferredPoHeaderID = parsePreferredPoHeaderId(
        editedFields.purchaseOrderNumber ?? invoice?.purchaseOrderNumber
    );

    const invoiceType = invoice?.invoiceType;
    const nexLineQuantityBasis = buildOpts?.nexLineQuantityBasis ?? 'qtyRec';
    const grouped = new Map<string, InvoicePartWithNex[]>();

    const ingestPo = (po: any, tagReturnsSource: boolean) => {
        const vendorName = String(po?.vendorName ?? '').trim();
        const detailLines = Array.isArray(po?.detailLines) ? po.detailLines : [];
        if (!vendorName || detailLines.length === 0) return;

        const linesOut: InvoicePartWithNex[] = [];
        for (const dl of detailLines) {
            const src = tagReturnsSource ? ('returns' as const) : undefined;
            linesOut.push(
                mapDetailLineToInvoicePartWithNex(
                    dl,
                    po,
                    vendorName,
                    invoiceType,
                    src,
                    nexLineQuantityBasis,
                ),
            );
        }
        if (linesOut.length === 0) return;
        const existing = grouped.get(vendorName) ?? [];
        existing.push(...linesOut);
        grouped.set(vendorName, existing);
    };

    for (const po of rawReturns as any[]) {
        if (scopeToInvoiceContext && !poMatchesInvoiceContext(po, supplierTrim, preferredPoHeaderID)) continue;
        ingestPo(po, invoiceType === 'return_slip');
    }

    return Array.from(grouped.entries())
        .map(([vendorName, lines]) => ({ vendorName, lines }))
        .sort((a, b) => a.vendorName.localeCompare(b.vendorName, undefined, { sensitivity: 'base' }));
}

function countNexOptionLines(
    buckets: Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }> | undefined
): number {
    if (!buckets?.length) return 0;
    return buckets.reduce((n, s) => n + (s.lines?.length ?? 0), 0);
}

export interface GetFlaggedItemMatchOptionsParams {
    partIndex: number;
    item: InvoicePart | undefined;
    issues: any[];
    choiceIssue: any;
    isUserUnmatchedLine: boolean;
    prevMatchedNex: any;
    previouslyMatchedNexLines: Record<number, any>;
    localMatchedNexOverrides: Record<number, any>;
    unmatchedPartIndices?: Set<number>;
    invoice: InvoiceDetailResponse | undefined;
    editedFields: Partial<InvoiceDetailResponse>;
    effectiveMatchedNexLines?: Array<{ partIndex: number; nexLine: any }>;
    /**
     * Current line row → last-saved line index. After delete/reorder, a saved match whose
     * backend index is absent here belongs to a removed row and must re-enter the pool.
     */
    lineItemBackendIndexByCurrentIndex?: (number | undefined)[];
    nexLineQuantityBasis?: NexLineQuantityBasis;
}

export interface GetFlaggedItemMatchOptionsResult {
    options: InvoicePartWithNex[];
    vendorName: string;
    suggestedPartNumber: string | undefined;
    baseOptionsFromSuggestion: InvoicePart[];
    singleVendorSection: { vendorName: string; lines: InvoicePart[]; noRemaining?: boolean } | null;
    nexOptionsByVendor: Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }> | undefined;
    fromIssueWithOptions: boolean;
    details: any;
    issueWithOptions: any;
    missingInNexIssue: any;
    issueOfRelevantType: any;
    showUnifiedModal: boolean;
    allPrevMatchedOptions: InvoicePartWithNex[];
    currentPrevMatchedOption: InvoicePartWithNex | null;
    userUnmatchedOptions: InvoicePart[];
    userUnmatchedVendorName: string;
    isBestMatch: (opt: InvoicePart) => boolean;
}

export function getFlaggedItemMatchOptions(params: GetFlaggedItemMatchOptionsParams): GetFlaggedItemMatchOptionsResult | null {
    const {
        partIndex,
        item,
        issues,
        choiceIssue,
        isUserUnmatchedLine,
        prevMatchedNex,
        previouslyMatchedNexLines,
        localMatchedNexOverrides,
        unmatchedPartIndices,
        invoice,
        editedFields,
        effectiveMatchedNexLines,
        lineItemBackendIndexByCurrentIndex,
        nexLineQuantityBasis = 'qtyRec',
    } = params;

    if (!item) return null;

    const canonicalPartIndex = globalPartIndexForMatchedNexLines(partIndex, invoice, editedFields);
    const isUnmatchedIndex = (idx: number): boolean => {
        if (!unmatchedPartIndices || unmatchedPartIndices.size === 0) return false;
        if (unmatchedPartIndices.has(idx)) return true;
        const canonical = globalPartIndexForMatchedNexLines(idx, invoice, editedFields);
        return unmatchedPartIndices.has(canonical);
    };

    const matchedToOtherIndices = new Set<string>();
    const backendMatchedForExclusion = effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const savedLineCount = invoice?.lineItems?.length ?? 0;
    backendMatchedForExclusion.forEach((m: { partIndex: number; nexLine: any }) => {
        const currentIdx = currentIndexForStoredLineMatch(
            m.partIndex,
            savedLineCount,
            lineItemBackendIndexByCurrentIndex,
        );
        // Deleted line item: its NEX line goes back into the suggestion pool.
        if (currentIdx == null) return;
        if (isUnmatchedIndex(currentIdx)) return;
        const canonicalM = globalPartIndexForMatchedNexLines(currentIdx, invoice, editedFields);
        if (canonicalM !== canonicalPartIndex && m.nexLine) matchedToOtherIndices.add(getNexKey(m.nexLine));
    });
    for (const [indexStr, nexLine] of Object.entries(localMatchedNexOverrides)) {
        const matchedIdx = parseInt(indexStr, 10);
        if (Number.isNaN(matchedIdx) || isUnmatchedIndex(matchedIdx)) continue;
        const canonicalM = globalPartIndexForMatchedNexLines(matchedIdx, invoice, editedFields);
        if (canonicalM !== canonicalPartIndex && nexLine) matchedToOtherIndices.add(getNexKey(nexLine));
    }
    const isMatchedElsewhere = (opt: any): boolean => matchedToOtherIndices.has(getNexKey(opt));

    const filterNexOptionsByVendorBuckets = (
        buckets: Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }> | undefined
    ): Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }> | undefined => {
        if (!buckets?.length) return undefined;
        const out = buckets
            .map((v) => ({
                ...v,
                lines: (v.lines ?? []).filter((l) => !isMatchedElsewhere(l)),
            }))
            .filter((v) => (v.lines?.length ?? 0) > 0 || v.noRemaining);
        return out.length > 0 ? out : undefined;
    };

    const hasActualNexOptions = (nex: any) =>
        nex?.issueDetails?.nexOptionsByVendor?.some((v: any) => v.lines?.length > 0);
    const missingInNexIssue = issues.find((i: any) => i.issueType === 'missing_in_nex');
    const issueWithActualNexOptions = issues.find(hasActualNexOptions);
    const issueOfRelevantType = issues.find((i: any) =>
        i.issueType === 'missing_in_nex' ||
        i.issueType === 'different_part_names' ||
        i.issueType === 'different_positions'
    );
    const anyIssueWithNexOptions = invoice?.reconciliationIssues?.flaggedParts?.find(hasActualNexOptions);
    const rawNexOptionsByVendor =
        issueWithActualNexOptions?.issueDetails?.nexOptionsByVendor ??
        anyIssueWithNexOptions?.issueDetails?.nexOptionsByVendor ??
        issueOfRelevantType?.issueDetails?.nexOptionsByVendor ??
        missingInNexIssue?.issueDetails?.nexOptionsByVendor;

    let fullFromStoredNex = buildFullNexOptionsByVendorFromInvoice(invoice, editedFields, {
        scopeToInvoiceContext: true,
        nexLineQuantityBasis,
    });
    let fullBucketsFiltered = filterNexOptionsByVendorBuckets(
        fullFromStoredNex.length > 0 ? fullFromStoredNex : undefined
    );
    if (countNexOptionLines(fullBucketsFiltered) === 0) {
        fullFromStoredNex = buildFullNexOptionsByVendorFromInvoice(invoice, editedFields, {
            scopeToInvoiceContext: false,
            nexLineQuantityBasis,
        });
        fullBucketsFiltered = filterNexOptionsByVendorBuckets(
            fullFromStoredNex.length > 0 ? fullFromStoredNex : undefined
        );
    }
    const issueBucketsFiltered = filterNexOptionsByVendorBuckets(rawNexOptionsByVendor as any);

    let nexOptionsByVendor: NexVendorBucket[] | undefined =
        countNexOptionLines(fullBucketsFiltered) > 0
            ? fullBucketsFiltered
            : issueBucketsFiltered ?? fullBucketsFiltered;
    nexOptionsByVendor = collapseNexOptionsByVendorWhenSupplierResolved(nexOptionsByVendor, invoice, editedFields);
    const issueWithOptions = choiceIssue ?? issues.find((i: any) =>
        (i.issueType === 'missing_in_nex' || i.issueType === 'different_part_names' || i.issueType === 'different_positions') &&
        (i.issueDetails?.suggestedPartNumber || (i.issueDetails?.suggestedNexLinesFromVendor?.length) || i.issueDetails?.suggestedLine || i.issueDetails?.nexOptionsByVendor?.length)
    );
    const singleVendorSection = nexOptionsByVendor?.length === 1 ? nexOptionsByVendor[0] : null;
    const fromIssueWithOptions = issueWithOptions != null;
    const details = issueWithOptions?.issueDetails ?? missingInNexIssue?.issueDetails ?? {};
    const suggestedLine = details.suggestedLine;
    const suggestedNexLinesFromVendor: InvoicePart[] = (
        details.suggestedNexLinesFromVendor ??
        (suggestedLine ? [suggestedLine] : [])
    ).filter((l: InvoicePart) => !isMatchedElsewhere(l));
    const suggestedPartNumber = details.suggestedPartNumber;

    const allPrevMatchedOptions: InvoicePartWithNex[] = [];
    for (const [, nexLine] of Object.entries(previouslyMatchedNexLines)) {
        if (!nexLine) continue;
        const nex = nexLine as any;
        const prevQtyRec =
            nex.qtyRec != null && Number.isFinite(Number(nex.qtyRec)) ? Number(nex.qtyRec) : undefined;
        const prevQtyOnOrder =
            nex.qtyOnOrder != null && Number.isFinite(Number(nex.qtyOnOrder)) ? Number(nex.qtyOnOrder) : undefined;
        allPrevMatchedOptions.push({
            partNumber: nex.partNumber,
            title: nex.partDesc ?? nex.title ?? '',
            qty: getSignedNexLineDisplayQty(nex, invoice?.invoiceType, nexLineQuantityBasis),
            qtyRec: prevQtyRec,
            ...(prevQtyOnOrder != null ? { qtyOnOrder: prevQtyOnOrder } : {}),
            listPrice: nex.listPrice,
            unitBuyPrice: nex.unitCost ?? nex.unitBuyPrice,
            totalBuyPrice: nex.extendedCost ?? nex.totalBuyPrice,
            confidence: 1,
            poHeaderID: nex.poHeaderID,
            lineNumber: nex.lineNumber,
            unqSeq: nex.unqSeq,
            _poOrderedDate: nex._poOrderedDate,
            lineStatus: nex.lineStatus,
            _poVendorName: nex._poVendorName,
            ...(nex._nexMatchSource ? { _nexMatchSource: nex._nexMatchSource } : {}),
        });
    }

    const currentPrevMatchedOption = prevMatchedNex ? allPrevMatchedOptions.find(opt =>
        opt.poHeaderID === prevMatchedNex.poHeaderID &&
        opt.lineNumber === prevMatchedNex.lineNumber &&
        opt.unqSeq === prevMatchedNex.unqSeq
    ) ?? null : null;

    const getKey = (opt: any) => {
        const ext = opt as Record<string, unknown>;
        const poHeaderID = ext.poHeaderID ?? ext.po_header_id ?? '';
        const lineNumber = ext.lineNumber ?? ext.line_number ?? '';
        const unqSeq = ext.unqSeq ?? ext.unq_seq ?? '';
        return `${poHeaderID}-${lineNumber}-${unqSeq}-${opt.partNumber}`;
    };

    const getSignificantWords = (text: string | null | undefined): Set<string> =>
        new Set(
            String(text ?? '')
                .toLowerCase()
                .replace(/[^a-z0-9\s]/g, ' ')
                .split(/\s+/)
                .filter((w) => w.length >= 3)
        );

    const descriptionsOverlap = (a: string | null | undefined, b: string | null | undefined): boolean => {
        const aw = getSignificantWords(a);
        const bw = getSignificantWords(b);
        if (aw.size === 0 || bw.size === 0) return false;
        for (const w of Array.from(aw)) {
            if (bw.has(w)) return true;
            for (const nw of Array.from(bw)) {
                if (w.includes(nw) || nw.includes(w)) return true;
            }
        }
        return false;
    };

    const scoreMoneyCloseness = (a: number | null | undefined, b: number | null | undefined): number => {
        const av = Math.abs(Number(a ?? 0));
        const bv = Math.abs(Number(b ?? 0));
        if (av < 0.01 && bv < 0.01) return 1;
        if (av < 0.01 || bv < 0.01) return 0;
        const hi = Math.max(av, bv);
        const lo = Math.min(av, bv);
        return lo / hi;
    };

    const normalizePartForSimilarity = (value: string | null | undefined): string =>
        String(value ?? '')
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '');

    const scorePartNumberSimilarity = (a: string | null | undefined, b: string | null | undefined): number => {
        const na = normalizePartForSimilarity(a);
        const nb = normalizePartForSimilarity(b);
        if (!na || !nb) return 0;
        if (na === nb) return 1;
        if (na.includes(nb) || nb.includes(na)) return 0.85;
        return 0;
    };

    const nexQtyReceivedForCompare = (option: InvoicePartWithNex): number =>
        nexQtyReceivedForDisplay(option, nexLineQuantityBasis, invoice?.invoiceType);

    const scoreOptionRelevance = (invoiceLine: InvoicePart | undefined, option: InvoicePartWithNex): number => {
        if (!invoiceLine) return 0;
        const qtyScore =
            Math.abs(Number(invoiceLine.qty ?? 0)) === Math.abs(nexQtyReceivedForCompare(option)) ? 1 : 0;
        const descScore = descriptionsOverlap(invoiceLine.title, option.title) ? 1 : 0;
        const partScore = scorePartNumberSimilarity(invoiceLine.partNumber, option.partNumber);
        const buyScore = scoreMoneyCloseness(invoiceLine.unitBuyPrice, option.unitBuyPrice);
        const listScore = scoreMoneyCloseness(invoiceLine.listPrice, option.listPrice);
        const invTotal = Math.abs(Number(invoiceLine.totalBuyPrice ?? 0));
        const optTotal = Math.abs(Number(option.totalBuyPrice ?? 0));
        const totalScore = scoreMoneyCloseness(invTotal, optTotal);

        return (
            partScore * 6 +
            buyScore * 5 +
            totalScore * 4 +
            listScore * 2 +
            qtyScore * 1.5 +
            descScore * 1
        );
    };

    const mergeWithPreviouslyUnmatched = (baseOpts: InvoicePart[]): InvoicePartWithNex[] => {
        const result: InvoicePartWithNex[] = [];
        const seen = new Set<string>();
        for (const opt of baseOpts) {
            if (isMatchedElsewhere(opt)) continue;
            const key = getKey(opt);
            if (!seen.has(key)) {
                seen.add(key);
                result.push(opt as InvoicePartWithNex);
            }
        }
        // Keep the row's previously matched NEX line at the top after an unmatch action,
        // regardless of whether this line is in "user unmatched" mode.
        if (currentPrevMatchedOption && !isMatchedElsewhere(currentPrevMatchedOption)) {
            const key = getKey(currentPrevMatchedOption);
            if (!seen.has(key)) {
                seen.add(key);
                result.unshift(currentPrevMatchedOption);
            }
        }
        for (const opt of allPrevMatchedOptions) {
            if (opt === currentPrevMatchedOption) continue;
            if (isMatchedElsewhere(opt)) continue;
            const key = getKey(opt);
            if (!seen.has(key)) {
                seen.add(key);
                result.push(opt);
            }
        }
        return result;
    };

    const getUserUnmatchedOptions = (): { options: InvoicePartWithNex[]; vendorName: string } => {
        if (!isUserUnmatchedLine) return { options: [], vendorName: '' };
        if (nexOptionsByVendor && nexOptionsByVendor.length > 0) {
            const allLines: InvoicePartWithNex[] = [];
            for (const v of nexOptionsByVendor) {
                const vn = v?.vendorName ?? '';
                for (const l of v?.lines ?? []) {
                    allLines.push({ ...(l as InvoicePartWithNex), _poVendorName: (l as InvoicePartWithNex)._poVendorName ?? vn });
                }
            }
            const vn0 = nexOptionsByVendor[0]?.vendorName ?? '';
            return { options: mergeWithPreviouslyUnmatched(allLines), vendorName: vn0 };
        }
        const matchedNexLines = invoice?.reconciliationIssues?.matchedNexLines ?? [];
        if (matchedNexLines.length > 0) {
            const nexOptions: InvoicePart[] = matchedNexLines.map((m: any) => ({
                partNumber: m.nexLine?.partNumber ?? '',
                title: m.nexLine?.partDesc ?? m.nexLine?.title ?? '',
                qty: getSignedNexLineDisplayQty(m.nexLine, invoice?.invoiceType, nexLineQuantityBasis) || 1,
                listPrice: m.nexLine?.listPrice,
                unitBuyPrice: m.nexLine?.unitCost ?? m.nexLine?.unitBuyPrice,
                totalBuyPrice: m.nexLine?.extendedCost ?? m.nexLine?.totalBuyPrice,
                poHeaderID: m.nexLine?.poHeaderID,
                lineNumber: m.nexLine?.lineNumber,
                unqSeq: m.nexLine?.unqSeq,
                _poOrderedDate: m.nexLine?._poOrderedDate,
                lineStatus: m.nexLine?.lineStatus,
                _poVendorName: m.nexLine?._poVendorName,
                ...(m.nexLine?._nexMatchSource ? { _nexMatchSource: m.nexLine._nexMatchSource } : {}),
            } as InvoicePartWithNex));
            return { options: mergeWithPreviouslyUnmatched(nexOptions), vendorName: editedFields.supplierText ?? invoice?.supplierText ?? 'this vendor' };
        }
        if (allPrevMatchedOptions.length > 0) {
            return {
                options: mergeWithPreviouslyUnmatched([]),
                vendorName: editedFields.supplierText ?? invoice?.supplierText ?? 'this vendor'
            };
        }
        return { options: [], vendorName: '' };
    };

    const userUnmatchedData = getUserUnmatchedOptions();
    const userUnmatchedOptions = userUnmatchedData.options;
    const userUnmatchedVendorName = userUnmatchedData.vendorName;

    const suggestedVendorForOptions = (details.suggestedVendorName ?? '').trim();
    // Only real NEX line payloads belong in the match table. A bare suggestedPartNumber is not a PO
    // line (no poHeaderID / line / unqSeq), breaks isMatchedElsewhere keying, and was shown with
    // invoice pricing — which misled users when vendor lines were already exhausted.
    const baseOptionsFromSuggestion: InvoicePartWithNex[] = suggestedNexLinesFromVendor.map((l) => ({
        ...l,
        _poVendorName: suggestedVendorForOptions || undefined,
    }));
    const vendorLines: InvoicePart[] = singleVendorSection?.lines ?? [];
    const vendorLinesWithVendor: InvoicePartWithNex[] = vendorLines.map((l) => ({ ...l, _poVendorName: singleVendorSection?.vendorName }));
    const baseOptions = vendorLinesWithVendor.length > 0 ? vendorLinesWithVendor : (baseOptionsFromSuggestion.length > 0 ? baseOptionsFromSuggestion : []);

    let options: InvoicePartWithNex[] = [];
    if (baseOptions.length > 0) {
        options = mergeWithPreviouslyUnmatched(baseOptions);
    } else if (userUnmatchedOptions.length > 0) {
        options = userUnmatchedOptions;
    } else {
        // Do not offer lines already matched to other invoice rows as "remaining" PO suggestions.
        options = mergeWithPreviouslyUnmatched([]);
    }

    const vendorName = fromIssueWithOptions
        ? ((details.suggestedVendorName ?? editedFields.supplierText ?? invoice?.supplierText ?? 'this vendor') as string).trim() || 'this vendor'
        : (singleVendorSection?.vendorName ?? (userUnmatchedVendorName || (editedFields.supplierText ?? invoice?.supplierText ?? 'this vendor')));

    const preferredPo = parsePreferredPoHeaderId(editedFields.purchaseOrderNumber ?? invoice?.purchaseOrderNumber);
    if (preferredPo != null && options.length > 1) {
        options = [...options].sort((a, b) => {
            const ao = a as InvoicePartWithNex;
            const bo = b as InvoicePartWithNex;
            const am = ao.poHeaderID === preferredPo ? 0 : 1;
            const bm = bo.poHeaderID === preferredPo ? 0 : 1;
            if (am !== bm) return am - bm;
            return 0;
        });
    }

    if (options.length > 1) {
        options = [...options].sort((a, b) => {
            const bs = scoreOptionRelevance(item, b);
            const as = scoreOptionRelevance(item, a);
            if (bs !== as) return bs - as;
            return 0;
        });
    }

    // Final pin: if this line previously had a selected NEX match and it's still a valid option,
    // keep it first so the modal "Best match" aligns with the user's last accepted match.
    if (currentPrevMatchedOption && options.length > 1) {
        const prevKey = getKey(currentPrevMatchedOption);
        const prevIdx = options.findIndex((opt) => getKey(opt) === prevKey);
        if (prevIdx > 0) {
            const pinned = options[prevIdx];
            options = [pinned, ...options.slice(0, prevIdx), ...options.slice(prevIdx + 1)];
        }
    }

    const showUnifiedModal = options.length > 0;
    const sameOption = (a: InvoicePart, b: InvoicePart) => a.partNumber === b.partNumber && a.title === b.title && a.unitBuyPrice === b.unitBuyPrice;
    const isBestMatch = (opt: InvoicePart) => options[0] != null && sameOption(opt, options[0]);

    return {
        options,
        vendorName,
        suggestedPartNumber,
        baseOptionsFromSuggestion,
        singleVendorSection: singleVendorSection ?? null,
        nexOptionsByVendor,
        fromIssueWithOptions,
        details,
        issueWithOptions: issueWithOptions ?? null,
        missingInNexIssue: missingInNexIssue ?? null,
        issueOfRelevantType: issueOfRelevantType ?? null,
        showUnifiedModal,
        allPrevMatchedOptions,
        currentPrevMatchedOption,
        userUnmatchedOptions,
        userUnmatchedVendorName,
        isBestMatch,
    };
}

/** Sentinel when CIECA/search hide every Flagged Item row — Match must not fall back to index 0. */
export const NO_VISIBLE_NEX_OPTION_KEY = '-1';
export const NO_VISIBLE_NEX_ACCORDION_KEY = '-1--1';

/** Keep Match pointed at a currently visible unified-table row, or the none-sentinel. */
export function firstVisibleNexOptionKey(
    visibleIndices: number[],
    selectedKey: string | null,
): string {
    if (visibleIndices.length === 0) return NO_VISIBLE_NEX_OPTION_KEY;
    const sel = parseInt(selectedKey ?? '0', 10);
    if (visibleIndices.includes(sel)) return String(sel);
    return String(visibleIndices[0]);
}

/** Keep Match pointed at a currently visible accordion row, or the none-sentinel. */
export function firstVisibleNexAccordionKey(
    visibleKeys: string[],
    selectedKey: string | null,
): string {
    if (visibleKeys.length === 0) return NO_VISIBLE_NEX_ACCORDION_KEY;
    const k = selectedKey ?? '0-0';
    if (selectedKey === NO_VISIBLE_NEX_ACCORDION_KEY || !visibleKeys.includes(k)) {
        return visibleKeys[0];
    }
    return k;
}

/** Resolve effectiveSelected from options and selectedNexOptionKey */
export function getEffectiveSelected(
    options: InvoicePartWithNex[],
    selectedNexOptionKey: string | null
): InvoicePartWithNex | null {
    if (options.length === 0) return null;
    if (selectedNexOptionKey === NO_VISIBLE_NEX_OPTION_KEY) return null;
    const selectedIdx = parseInt(selectedNexOptionKey ?? '0', 10);
    if (Number.isNaN(selectedIdx) || selectedIdx < 0) return null;
    if (selectedIdx >= options.length) return options[0] ?? null;
    return options[selectedIdx] ?? null;
}

/** Resolve selected NEX row from multi-vendor accordion key `${sectionIdx}-${lineIdx}` (same as FlaggedItemContent). */
export function getEffectiveAccordionSelected(
    nexOptionsByVendor: Array<{ vendorName: string; lines: InvoicePart[]; noRemaining?: boolean }> | undefined,
    selectedNexOptionAccordionKey: string | null
): InvoicePartWithNex | null {
    if (!nexOptionsByVendor?.length) return null;
    const total = nexOptionsByVendor.reduce((n, sec) => n + (sec.lines?.length ?? 0), 0);
    if (total === 0) return null;
    if (selectedNexOptionAccordionKey === NO_VISIBLE_NEX_ACCORDION_KEY) return null;
    const k = selectedNexOptionAccordionKey ?? '0-0';
    const parts = k.split('-').map(Number);
    const s = parts[0];
    const l = parts[1];
    if (!Number.isNaN(s) && !Number.isNaN(l)) {
        if (s < 0 || l < 0) return null;
        const sec = nexOptionsByVendor[s];
        const line = sec?.lines?.[l] as InvoicePartWithNex | undefined;
        if (line != null) {
            return {
                ...line,
                _poVendorName: line._poVendorName ?? sec.vendorName,
            };
        }
    }
    for (const sec of nexOptionsByVendor) {
        if (sec.lines?.length) {
            const line = sec.lines[0] as InvoicePartWithNex;
            return { ...line, _poVendorName: line._poVendorName ?? sec.vendorName };
        }
    }
    return null;
}
